import React, { useContext, useEffect, useRef, useState } from "react";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { HeaderContext } from "../../../../HeaderContext";
import { errorNoti } from "../../../../base/BasicNotifications";
import AnnualOkrSkeleton from "../../AnnualOkr/AnnualOkrSkeleton";
import { readError } from "../_config/quarterlyOkrConfig";
import quarterlyOkrStore, {
  useQuarterlyOkrStore,
} from "../_store/quarterlyOkrStore";
import ClientCard from "./ClientCard/ClientCard";
import "../../AnnualOkr/AnnualOkr.scss";

// ═══════════════════════════════════════════════════════════════════════════
// Quarterly OKR coach dashboard (§9.1) — one card per client: the latest
// quarter and its CTA on the row, earlier quarters in the accordion. The
// Annual OKR dashboard's card (`aok-*`, imported, under its `#annual-okr`
// id) so the coach's dashboards read as one product.
// ═══════════════════════════════════════════════════════════════════════════

const selectClients = (state) => state.clients;

// Keep in sync with the accordion transition in AnnualOkr.scss.
const ACCORDION_TRANSITION_MS = 350;

const QuarterlyOkrDashboard = () => {
  const HeaderMetaData = useContext(HeaderContext);
  const clients = useQuarterlyOkrStore(selectClients);
  const [failed, setFailed] = useState(false);
  // One card open at a time; the next opens once the last has closed, so the
  // list doesn't jump (same as the sibling dashboards).
  const [openId, setOpenId] = useState(null);
  const openTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(openTimerRef.current), []);

  const handleToggle = (id) => {
    clearTimeout(openTimerRef.current);
    if (openId === id) return setOpenId(null);
    if (openId === null) return setOpenId(id);
    setOpenId(null);
    openTimerRef.current = setTimeout(
      () => setOpenId(id),
      ACCORDION_TRANSITION_MS,
    );
    return undefined;
  };

  useEffect(() => {
    document.title = "Art of Scale | Quarterly OKRs";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Quarterly OKRs" });
    quarterlyOkrStore
      .getState()
      .fetchDashboard()
      .catch((error) => {
        setFailed(true);
        errorNoti(readError(error).message);
      });
    // eslint-disable-next-line
  }, []);

  // `null` = not fetched yet, so the skeleton shows on first paint rather
  // than the empty state flashing first.
  const loading = clients === null && !failed;
  const list = clients || [];

  return (
    <div>
      <ProjectHeader handleNavigation={() => {}} showBack={false} />
      <div id="annual-okr">
        <div className="aok-header">
          <div className="aok-title">Quarterly OKRs</div>
          <div className="aok-subtitle">
            {loading
              ? ""
              : `${list.length} ${list.length === 1 ? "client" : "clients"}`}
          </div>
        </div>

        {loading ? (
          <AnnualOkrSkeleton count={4} />
        ) : list.length === 0 ? (
          <div className="aok-empty-state">
            No clients are planning their quarters with the AI coach yet.
          </div>
        ) : (
          <div className="aok-list aok-fade-in">
            {list.map((client) => (
              <ClientCard
                key={client.company_id}
                client={client}
                isOpen={openId === client.company_id}
                onToggle={() => handleToggle(client.company_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuarterlyOkrDashboard;
